import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { scroller } from "react-scroll";
import {
  splitLocalePath,
  useLocalePath,
  useTranslation,
} from "@thyra/landing-i18n";
import { getAppUrl } from "../env";
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "./ui/resizable-navbar";

const SCROLL_THRESHOLD = 24;
const SECTION_SCROLL_OPTIONS = { smooth: true, duration: 650, offset: -88 };

const NAV_LINKS = [
  { key: "product", link: "/#product" },
  { key: "setup", link: "/#how-it-works" },
  { key: "pricing", link: "/#pricing" },
  { key: "business", link: "/business" },
  { key: "about", link: "/about" },
  { key: "blog", link: "/blog" },
  { key: "contact", link: "/contact" },
] as const;

const earlyAccessNavButtonBaseClass =
  "shrink-0 items-center justify-center rounded-full bg-primary px-3 py-2 text-xs font-semibold text-white antialiased shadow-sm transition-opacity hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:px-4 sm:text-sm";
const earlyAccessNavButtonClass = `inline-flex ${earlyAccessNavButtonBaseClass}`;
// On narrow phones the pill would overlap the logo, so it moves into the menu.
const earlyAccessMobileHeaderButtonClass = `hidden min-[480px]:inline-flex ${earlyAccessNavButtonBaseClass}`;

/** True when `link` targets a section of the home page we are already on. */
function isInPageAnchor(link: string, pathname: string): boolean {
  if (splitLocalePath(pathname).path !== "/") return false;
  const [path, hash] = link.split("#");
  return (
    splitLocalePath(path || "/").path === "/" && hash != null && hash.length > 0
  );
}

export function Header() {
  const location = useLocation();
  const { t } = useTranslation("common");
  const lp = useLocalePath();
  const navItems = NAV_LINKS.map(({ key, link }) => ({
    name: t(`nav.${key}`),
    link: lp(link),
  }));
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: { link: string },
  ) => {
    if (isInPageAnchor(item.link, location.pathname)) {
      e.preventDefault();
      const hash = item.link.split("#")[1];
      if (hash) scroller.scrollTo(hash, SECTION_SCROLL_OPTIONS);
    }
  };

  return (
    <Navbar>
      <NavBody scrolled={scrolled}>
        <div className="flex w-full items-center justify-between gap-3 px-1 sm:px-2">
          <div className="shrink-0">
            <NavbarLogo />
          </div>
          <NavItems
            items={navItems}
            onItemClick={handleNavClick}
            className="min-w-0 flex-1 justify-center"
          />
          <a href={getAppUrl()} className={earlyAccessNavButtonClass}>
            {t("nav.cta")}
          </a>
        </div>
      </NavBody>

      <MobileNav>
        <MobileNavHeader>
          <NavbarLogo />
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={getAppUrl()}
              className={earlyAccessMobileHeaderButtonClass}
            >
              {t("nav.cta")}
            </a>
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </div>
        </MobileNavHeader>

        <MobileNavMenu isOpen={isMobileMenuOpen}>
          {navItems.map((item, idx) => (
            <a
              key={`mobile-link-${idx}`}
              href={item.link}
              onClick={(e) => {
                if (isInPageAnchor(item.link, location.pathname)) {
                  e.preventDefault();
                  const hash = item.link.split("#")[1];
                  if (hash) scroller.scrollTo(hash, SECTION_SCROLL_OPTIONS);
                }
                setIsMobileMenuOpen(false);
              }}
              className="relative text-muted-foreground hover:text-foreground py-2"
            >
              <span className="block font-medium">{item.name}</span>
            </a>
          ))}
          <a
            href={getAppUrl()}
            className={`${earlyAccessNavButtonClass} min-[480px]:hidden mt-2 w-full`}
          >
            {t("nav.cta")}
          </a>
        </MobileNavMenu>
      </MobileNav>
    </Navbar>
  );
}
